import anfdConfig from 'Components/Worksheets/AsphaltNuclearFieldDensityWorksheet/config';
import amddConfig from 'Components/Worksheets/AssignedMaximumDryDensityWorksheet/config';
import alConfig from 'Components/Worksheets/AtterbergLimitWorksheet/config';
import bcConfig from 'Components/Worksheets/BinderContentWorksheet/config';
import { ConfigObject, GetConfigFunction, GetConfigFunctionArgs } from 'Components/Worksheets/Config';
import cwdConfig from 'Components/Worksheets/ConvertedWetDensityWorksheet/config';
import dcpConfig from 'Components/Worksheets/DynamicConePenetrometerWorksheet/config';
import ecConfig from 'Components/Worksheets/EmersonClassificationWorksheet/config';
import handConfig from 'Components/Worksheets/HandShearVaneWorksheet/config';
import lsConfig from 'Components/Worksheets/LinearShrinkage/config';
import laConfig from 'Components/Worksheets/LosAngelesValueWorksheet/config';
import mddConfig from 'Components/Worksheets/MaximumDryDensityWorksheet/config';
import mcvConfig from 'Components/Worksheets/MoistureConditionValueWorksheet/config';
import mcConfig from 'Components/Worksheets/MoistureContentWorksheet/config';
import nfdConfig from 'Components/Worksheets/NuclearFieldDensityWorksheet/config';
import organicConfig from 'Components/Worksheets/OrganicContentWorksheet/config';
import ocConfig from 'Components/Worksheets/OversizeCorrectionWorksheet/config';
import odConfig from 'Components/Worksheets/OversizeDeterminationWorksheet/config';
import pdConfig from 'Components/Worksheets/ParticleDensityWorksheet/config';
import plConfig from 'Components/Worksheets/PlateLoadingWorksheet/config';
import psdHyrdometerConfig from 'Components/Worksheets/PsdHydrometerWorksheet/config';
import psdConfig from 'Components/Worksheets/PsdSieveAnalysisWorksheet/config';
import rcConfig from 'Components/Worksheets/RelativeCompactionWorksheet/config';
import samplingCertficateConfig from 'Components/Worksheets/SamplingCertificate/config';
import sandrepConfig from 'Components/Worksheets/SandReplacementWorksheet/config';
import uscEstimatedAlConfig from 'Components/Worksheets/SoilClassificationEstimatedAlWorksheet/config';
import uscEstimatedPsdConfig from 'Components/Worksheets/SoilClassificationEstimatedPsdWorksheet/config';
import uscsConfig from 'Components/Worksheets/SoilClassificationWorksheet/config';
import sgcConfig from 'Components/Worksheets/SpecificGravityCoarseWorksheet/config';
import washConfig from 'Components/Worksheets/WashWorksheet/config';
import { hasValue } from 'Lib/Any';
import * as oPath from 'Lib/Object/Path/Immutable';
import { joinPaths } from 'Lib/Object/Path/shared';
import { IConfigFunctionArgs } from '../Core';

/**
 * Maps QestId of a test to a function that will return
 * the config object for that test
 */
export const QestIdToGetConfigFunctionLookup: {
  [qestId: string]: GetConfigFunction<GetConfigFunctionArgs>;
} = {
  ...lsConfig,
  ...mcConfig,
  ...alConfig,
  ...sgcConfig,
  ...mddConfig,
  ...organicConfig,
  ...psdConfig,
  ...plConfig,
  ...washConfig,
  ...uscsConfig,
  ...nfdConfig,
  ...handConfig,
  ...sandrepConfig,
  ...dcpConfig,
  ...anfdConfig,
  ...cwdConfig,
  ...samplingCertficateConfig,
  ...pdConfig,
  ...mcvConfig,
  ...laConfig,
  ...rcConfig,
  ...odConfig,
  ...ocConfig,
  ...ecConfig,
  ...psdHyrdometerConfig,
  ...uscEstimatedAlConfig,
  ...uscEstimatedPsdConfig,
  ...amddConfig,
  ...bcConfig,
};

/**
 * Returns the "config" object (which maps field name to injected properties) for a test by {@link qestId}
 *
 * @param qestId The type id ("QestId") of the test TODO: should be a number
 * @param prefix The prefix for field names in the config object. This should be the path from the root "test model" to the
 *  "test data model" for the data of this test (`data` by default)
 * @param additionalGetConfigFunctionArgs Provides additional arguments to customise the returned config object,
 * specific to individual test methods
 */
export const getConfigForTest = (
  qestId: string,
  prefix: string = 'data',
  additionalGetConfigFunctionArgs?: GetConfigFunctionArgs
) => {
  if (hasValue(additionalGetConfigFunctionArgs))
    return QestIdToGetConfigFunctionLookup[qestId]?.(prefix, additionalGetConfigFunctionArgs);

  return QestIdToGetConfigFunctionLookup[qestId]?.(prefix);
};

/**
 * Returns the "config" object (which maps field name to injected properties)
 * for the MeasuredTest component of an associated test model
 *
 * @param qestId The type id ("QestId") of the test TODO: should be a number
 * @param prefix The prefix for field names in the config object. This should be the path from the root "test model" to the
 *  "associated test model" that encapsulates the measured test.
 * @param parameters Provides additional arguments to customise the returned config object,
 * specific to individual test methods
 */
export const getConfigForMeasuredTest = (qestId: string, prefix: string, parameters?: GetConfigFunctionArgs) => {
  const path = joinPaths(prefix, 'MeasuredTest');
  return getConfigForTest(qestId, path, parameters);
};

/**
 * Returns the "config" object (which maps field name to injected properties)
 * for the MeasuredTest component of an associated test model
 * where multiple test types are allowed
 *
 * @param allowedQestIds The type id ("QestId") of all allowed test types TODO: should be a number
 * @param prefix The prefix for field names in the config object. This should be the path from the root "test model" to the
 *  "associated test model" that encapsulates the measured test.
 * @param parameters Provides additional arguments to customise the returned config object,
 * specific to individual test methods
 */
export const getConfigForMeasuredTests = (
  allowedQestIds: string[],
  prefix: string,
  parameters?: GetConfigFunctionArgs
): ConfigObject => {
  if (allowedQestIds.length < 1) return {};

  if (allowedQestIds.length === 1) return getConfigForMeasuredTest(allowedQestIds[0], prefix, parameters);

  // Combine config objects so each element is a map from QestIds to the original config
  let configByQestId: {
    [key: string]: { [qestId: string]: ConfigObject[keyof ConfigObject] };
  } = {};
  allowedQestIds.forEach((i) => {
    Object.entries(getConfigForMeasuredTest(i, prefix, parameters)).forEach(([k, v]) => {
      configByQestId[k] = {
        ...(configByQestId[k] ?? {}),
        [i]: v,
      };
    });
  });

  // Produce a new config object where each path is mapped to a function
  // that returns the appropriate config for the selected test QestID
  return Object.fromEntries(
    Object.entries(configByQestId).map(([k, v]) => [
      k,
      (configArgs: IConfigFunctionArgs) => {
        var selectedTestQestId = oPath.get(
          configArgs.worksheet,
          joinPaths(prefix, 'MeasuredTestOptions', 'SelectedTestQestId')
        );
        var config = v[selectedTestQestId];
        return typeof config === 'function' ? config(configArgs) : config;
      },
    ])
  );
};

/**
 * Returns the config object (which maps field name to injected properties)
 * for the ImportedTest component of an associated test model
 *
 * @deprecated TODO: The approach of this function assumes that we want to apply the "test data model" config object
 * to the imported test. This is not true. We actually want the configuration for the "imported results model"
 * which will not necessarily match the structure of the test. This also does not support importing from multiple sources
 * (it always takes the config for one provided QestId).
 *
 * @param qestIdForConfig The type id ("QestId") of the test to apply the config for TODO: should be a number
 * @param prefix The prefix for field names in the config object. This should be the path from the root "test model" to the
 *  "associated test model" that encapsulates the imported test.
 * @param additionalQestIdsForImport QestIds of other tests that can be used as import sources (but whose config is not taken)
 * @returns
 */
export const getConfigForImportedTest = (
  qestIdForConfig: string | undefined,
  prefix: string,
  additionalQestIdsForImport?: string[]
) => {
  var path = joinPaths(prefix, 'ImportedTest');
  var testMethodIdConfig = { [path]: { testMethodIds: [qestIdForConfig, ...[additionalQestIdsForImport ?? []]] } };
  var config = qestIdForConfig ? getConfigForTest(qestIdForConfig, path) : {};
  return { ...config, ...testMethodIdConfig };
};
